import React, { useEffect, useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold, Italic, Strikethrough, Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Link as LinkIcon, Unlink,
  Undo2, Redo2, Code,
} from "lucide-react";

// TipTap-based rich text editor. Emits HTML via `onChange`.
export default function RichTextEditor({ value, onChange, placeholder = "Write your post here..." }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          rel: "noopener noreferrer",
          class: "text-[#C8102E] underline underline-offset-2 font-bold",
        },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "rte-content min-h-[280px] p-4 outline-none",
        "data-testid": "rte-content",
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html === "<p></p>" ? "" : html);
    },
  });

  // Sync when value changes from outside (e.g. loading a post to edit)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || "";
    if (next && current !== next) {
      editor.commands.setContent(next, false);
    }
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes("link").href;
    const url = window.prompt("Enter a URL", previous || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url, target: "_blank" }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="border-2 border-black rounded-lg bg-white overflow-hidden" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-1 border-b-2 border-black bg-zinc-50 px-2 py-2 sticky top-0">
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="Heading 1" testId="rte-h1"><Heading1 className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading 2" testId="rte-h2"><Heading2 className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Heading 3" testId="rte-h3"><Heading3 className="h-4 w-4" /></ToolbarBtn>
        <Sep />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold" testId="rte-bold"><Bold className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic" testId="rte-italic"><Italic className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough" testId="rte-strike"><Strikethrough className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive("code")} title="Inline code" testId="rte-code"><Code className="h-4 w-4" /></ToolbarBtn>
        <Sep />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bulleted list" testId="rte-ul"><List className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list" testId="rte-ol"><ListOrdered className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote" testId="rte-quote"><Quote className="h-4 w-4" /></ToolbarBtn>
        <Sep />
        <ToolbarBtn onClick={setLink} active={editor.isActive("link")} title="Add link" testId="rte-link"><LinkIcon className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().unsetLink().run()} title="Remove link" testId="rte-unlink" disabled={!editor.isActive("link")}><Unlink className="h-4 w-4" /></ToolbarBtn>
        <Sep />
        <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} title="Undo" testId="rte-undo" disabled={!editor.can().undo()}><Undo2 className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} title="Redo" testId="rte-redo" disabled={!editor.can().redo()}><Redo2 className="h-4 w-4" /></ToolbarBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

function ToolbarBtn({ children, onClick, active, title, disabled, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`h-8 w-8 inline-flex items-center justify-center rounded-md border-2 font-bold text-sm transition-colors ${
        active
          ? "border-black bg-[#FACC15] text-black"
          : "border-transparent text-zinc-700 hover:border-black hover:bg-white disabled:opacity-40 disabled:hover:border-transparent disabled:hover:bg-transparent"
      }`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}
function Sep() {
  return <span className="mx-1 h-6 w-px bg-zinc-300" />;
}
